import React, { Component } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import ReactRouterPropTypes from 'react-router-prop-types';
import { Helmet } from 'react-helmet-async';
import SecurePage from '../../hocs/secure-page';
import heapService from '../../services/heap.service';
import resolve from '../../services/route.service';
import routes from '../../routes';
import errors from '../../constants/errors';
import getStipulationsAction, { fulfillStipulationAction } from '../../redux/actions/stipulations/stipulations-actions';
import { updatePppObjectAction } from '../../redux/actions/paycheck-protection-plan/paycheck-protection-plan-actions';
import { getOpportunitiesAction } from '../../redux/actions/opportunities/opportunities-actions';
import CalculatePayrollDataComponent from '../../components/calculate-payroll-data/calculate-payroll-data';
import displayNames from '../../constants/displayNames';
import StandardLayout from '../../layouts/StandardLayout';

const successStipulationGroupName = 'Calculate Payroll Data';

class CalculatePayrollDataPage extends Component {
  static displayName = displayNames.CalculatePayrollDataPage;

  static propTypes = {
    accountsById: PropTypes.objectOf(PropTypes.shape({
      businessEntityType: PropTypes.string.isRequired,
    })),
    country: PropTypes.string.isRequired,
    environment: PropTypes.string.isRequired,
    fulfillStipulation: PropTypes.func.isRequired,
    fulfillStipulationResult: PropTypes.shape({
      error: PropTypes.bool,
      pending: PropTypes.bool,
    }),
    getOpportunities: PropTypes.func.isRequired,
    getStipulations: PropTypes.func.isRequired,
    history: ReactRouterPropTypes.history.isRequired,
    location: ReactRouterPropTypes.location.isRequired,
    match: PropTypes.shape({
      params: PropTypes.object,
    }),
    opportunitiesById: PropTypes.objectOf(PropTypes.shape({
      accountId: PropTypes.string,
      loanVersion: PropTypes.string,
      paycheckProtectionPlanId: PropTypes.string.isRequired,
    })),
    push: PropTypes.func.isRequired,
    stipulations: PropTypes.arrayOf(PropTypes.object),
    updatePppObjectFunc: PropTypes.func.isRequired,
  };

  static defaultProps = {
    fulfillStipulationResult: {},
    match: undefined,
    opportunitiesById: {},
    accountsById: {},
    stipulations: [],
  };

  constructor() {
    super();
    this.state = { submissionError: undefined };
  }

  async componentDidMount() {
    const {
      country,
      getOpportunities,
      getStipulations,
      opportunitiesById,
      match: { params: { opportunityId } },
    } = this.props;
    if (!opportunitiesById[opportunityId]) {
      await getOpportunities();
    }
    await getStipulations(opportunityId, country);
  }

  submit = async (answers) => {
    const {
      match, updatePppObjectFunc, opportunitiesById,
    } = this.props;

    const { opportunityId } = match.params;
    const { paycheckProtectionPlanId } = opportunitiesById[opportunityId];

    const updateDataObject = {
      ...answers.data,
      opportunityId,
      paycheckProtectionPlanId,
    };
    this.setState({ submissionError: undefined });

    let shouldFulfill = false;
    try {
      const result = await updatePppObjectFunc(updateDataObject);
      if (!result || result.error) {
        this.setState({ submissionError: errors.technicalIssue });
        return;
      }
      shouldFulfill = true;
    }
    catch (error) {
      this.setState({ submissionError: errors.technicalIssue });
      return;
    }

    this.redirectToSummaryConfirmation(shouldFulfill);
  }

  async redirectToSummaryConfirmation(shouldFulfillStipGroup = false) {
    const {
      country, push, match, fulfillStipulation, stipulations,
    } = this.props;
    const { opportunityId } = match.params;
    const RoutePayload = {
      country,
      successStipulationGroupName,
    };
    const stipulation = stipulations.find(({ stipulationMap: { name } }) => name === successStipulationGroupName);

    if (!stipulation) {
      return undefined;
    }

    if (shouldFulfillStipGroup) {
      await fulfillStipulation(opportunityId, stipulation.id);

      const { fulfillStipulationResult } = this.props;
      if (fulfillStipulationResult.error && !fulfillStipulationResult.pending) {
        return undefined;
      }
    }

    heapService.markUserStipulationAsCompleted(opportunityId, successStipulationGroupName);
    return push(resolve(routes.summary.path, RoutePayload));
  }

  render() {
    const {
      accountsById, opportunitiesById, match,
    } = this.props;
    const { submissionError } = this.state;

    const { opportunityId } = match.params;
    const opportunity = opportunitiesById[opportunityId];
    const loanVersion = opportunity?.loanVersion;
    const showPPPLoan = ['21PPPLoan', '21SDPPP'].includes(loanVersion);
    if (!showPPPLoan) {
      return null;
    }
    const isFirstDraw = loanVersion === '21PPPLoan';

    const hideThirdOption = ['Eligible self-employed individual', 'Sole Proprietorship', 'Independent contractor']
      .includes(accountsById[opportunity.accountId]?.businessEntityType);

    const calculatePayrollDataProps = {
      updatePppObjectFunc: this.submit,
      submissionError,
      isFirstDraw,
      hideThirdOption,
    };

    return (
      <>
        <div>
          <Helmet>
            <title>Calculate Payroll Data</title>
          </Helmet>
          <StandardLayout
            contentElement={<CalculatePayrollDataComponent {...calculatePayrollDataProps} />}
            sidebarElement={<></>}
          />
        </div>
      </>
    );
  }
}

function mapStateToProps(state) {
  return {
    environment: state.config.environment,
    fulfillStipulationResult: state.fulfillStipulation,
    loanForgiveness: state.entities.loanForgiveness,
    opportunitiesById: state.entities.opportunities?.byId,
    accountsById: state.entities.accounts?.byId,
    location: state.router.location,
    stipulations: state.stipulations.allStipulations,
  };
}

const mapDispatchToProps = {
  fulfillStipulation: fulfillStipulationAction,
  getOpportunities: getOpportunitiesAction,
  getStipulations: getStipulationsAction,
  updatePppObjectFunc: updatePppObjectAction,
};

export default connect(mapStateToProps, mapDispatchToProps)(SecurePage(CalculatePayrollDataPage));
